/**
 * ToolAccessTable - Unified tool access configuration table.
 *
 * Used in both:
 * - AI Agent OCTOPUS Tools tab (permanent agent grants)
 * - Chat McpConfigPanel (per-session tool config)
 *
 * Table rows: Module | Access (Read / Approve / Write) | Whose Data | Remove
 * Starts empty or pre-populated. Users add rows via "+ Add" button.
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, ShieldCheck, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../../components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { getModuleById, type ModuleConfig } from '../../config/modules.config';
import { MCP_TO_MODULE_ID } from './mcpModuleMapping';
import { filterAndSortBySearch } from '../../utils/searchScore';
import type { ToolAccessRow, McpAccessLevel, ToolDataScope } from './types';

/** Module metadata from /api/v1/mcp/modules */
export interface ToolAccessModule {
  name: string;
  display_name: string;
  description?: string;
  read_only?: boolean;
  /** Available tool scope keys for fine-grained service selection */
  tool_scopes?: string[];
}

interface ToolAccessTableProps {
  /** Current rows */
  rows: ToolAccessRow[];
  /** Called when rows change */
  onChange: (rows: ToolAccessRow[]) => void;
  /** Available modules that can be added */
  availableModules: ToolAccessModule[];
  /** Show "Agent's own" data scope option */
  showAgentScope?: boolean;
  /** Show the "Whose Data" column (false = always current user, hide column) */
  showWhoseData?: boolean;
  /** Current user info for scope display */
  currentUser?: { id: string; name: string };
  /** Whether current user is admin (shows "All" scope option) */
  isAdmin?: boolean;
  /** Read-only mode */
  readOnly?: boolean;
  /** Additional CSS class */
  className?: string;
  /** Compact mode for popover embedding */
  compact?: boolean;
  /** Show auto-share checkbox column (for agent permanent grants) */
  showAutoShare?: boolean;
}

/** Generate a unique ID for new rows */
function generateRowId(): string {
  return `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export const ToolAccessTable: React.FC<ToolAccessTableProps> = ({
  rows,
  onChange,
  availableModules,
  showAgentScope = false,
  showWhoseData = true,
  currentUser,
  isAdmin = false,
  readOnly = false,
  className,
  compact = false,
  showAutoShare = false,
}) => {
  const { t } = useTranslation('aiChat');
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [moduleSearch, setModuleSearch] = useState('');

  // Modules already in the table
  const usedModules = useMemo(
    () => new Set(rows.map((r) => r.module_name)),
    [rows],
  );

  // Modules available to add (not yet in table)
  const addableModules = useMemo(
    () => availableModules.filter((m) => !usedModules.has(m.name)),
    [availableModules, usedModules],
  );

  // Filtered addable modules for search
  const filteredAddableModules = useMemo(
    () => filterAndSortBySearch(addableModules, moduleSearch, (m) => m.display_name),
    [addableModules, moduleSearch],
  );

  const updateRow = useCallback(
    (rowId: string, updates: Partial<ToolAccessRow>) => {
      onChange(rows.map((r) => (r.id === rowId ? { ...r, ...updates } : r)));
    },
    [rows, onChange],
  );

  const removeRow = useCallback(
    (rowId: string) => {
      onChange(rows.filter((r) => r.id !== rowId));
    },
    [rows, onChange],
  );

  const addRow = useCallback(
    (moduleName: string) => {
      const newRow: ToolAccessRow = {
        id: generateRowId(),
        module_name: moduleName,
        access_level: 'read',
        data_scope: showAgentScope ? 'self' : 'user',
        scope_user_id: showAgentScope ? undefined : currentUser?.id,
        scope_user_name: showAgentScope ? undefined : currentUser?.name,
      };
      onChange([...rows, newRow]);
      setIsAddingRow(false);
    },
    [rows, onChange, showAgentScope, currentUser],
  );

  const getModuleConfig = (moduleName: string): ModuleConfig | undefined => {
    const moduleId = MCP_TO_MODULE_ID[moduleName] || moduleName;
    return getModuleById(moduleId);
  };

  const getModuleDisplayName = (moduleName: string): string => {
    const mod = availableModules.find((m) => m.name === moduleName);
    const name = mod ? mod.display_name : moduleName.charAt(0).toUpperCase() + moduleName.slice(1).replace(/_/g, ' ');
    return name.replace(/^PanLuma\s+/i, '');
  };

  const isModuleReadOnly = (moduleName: string): boolean => {
    const mod = availableModules.find((m) => m.name === moduleName);
    return !!mod?.read_only;
  };

  const getModuleToolScopes = (moduleName: string): string[] | undefined => {
    const mod = availableModules.find((m) => m.name === moduleName);
    return mod?.tool_scopes;
  };

  return (
    <div className={cn('space-y-1', className)}>
      {/* Header row */}
      <div
        className={cn(
          'grid items-center gap-1.5 px-2 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider',
          showWhoseData
            ? showAutoShare
              ? 'grid-cols-[2fr_auto_1fr_auto_24px]'
              : 'grid-cols-[2fr_auto_1fr_24px]'
            : 'grid-cols-[1fr_auto_24px]',
        )}
      >
        <span>{t('toolAccess.module', 'Module')}</span>
        <span className="w-[130px] text-center">{t('toolAccess.access', 'Access')}</span>
        {showWhoseData && <span>{t('toolAccess.whoseData', 'Whose Data')}</span>}
        {showAutoShare && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="w-[52px] text-center cursor-help">{t('toolAccess.share', 'Share')}</span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">{t('toolAccess.shareTooltip', 'Auto-share entities created by the agent with you')}</p>
            </TooltipContent>
          </Tooltip>
        )}
        <span />
      </div>

      {/* Rows */}
      {rows.map((row) => (
        <ToolAccessRowComponent
          key={row.id}
          row={row}
          onUpdate={(updates) => updateRow(row.id, updates)}
          onRemove={() => removeRow(row.id)}
          getModuleConfig={getModuleConfig}
          getModuleDisplayName={getModuleDisplayName}
          isModuleReadOnly={isModuleReadOnly(row.module_name)}
          moduleToolScopes={getModuleToolScopes(row.module_name)}
          showWhoseData={showWhoseData}
          showAgentScope={showAgentScope}
          currentUser={currentUser}
          isAdmin={isAdmin}
          readOnly={readOnly}
          compact={compact}
          showAutoShare={showAutoShare}
        />
      ))}

      {/* Empty state */}
      {rows.length === 0 && !isAddingRow && (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <ShieldCheck className="h-8 w-8 text-muted-foreground/40" />
          <p className="text-xs text-muted-foreground">
            {t('toolAccess.empty', 'No tools configured. Add a module to get started.')}
          </p>
        </div>
      )}

      {/* Add button with searchable module picker */}
      {!readOnly && addableModules.length > 0 && (
        <Popover open={isAddingRow} onOpenChange={setIsAddingRow}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground hover:text-foreground border border-dashed border-transparent hover:border-border"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {t('toolAccess.addModule', 'Add module')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={t('toolAccess.searchModules', 'Search modules...')}
                value={moduleSearch}
                onValueChange={setModuleSearch}
              />
              <CommandList>
                <CommandEmpty>{t('toolAccess.noModulesFound', 'No modules found')}</CommandEmpty>
                <CommandGroup>
                  {filteredAddableModules.map((mod) => {
                    const modConfig = getModuleConfig(mod.name);
                    const color = modConfig?.color ?? '#616161';
                    return (
                      <CommandItem
                        key={mod.name}
                        value={mod.name}
                        onSelect={() => {
                          addRow(mod.name);
                          setModuleSearch('');
                        }}
                      >
                        <span
                          className="w-5 h-5 rounded flex items-center justify-center shrink-0 [&>svg]:h-3 [&>svg]:w-3"
                          style={{ backgroundColor: `${color}20`, color }}
                        >
                          {modConfig?.icon}
                        </span>
                        <span className="truncate">{mod.display_name.replace(/^PanLuma\s+/i, '')}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// Tool-scope keys recognized by the scope picker. Human-readable labels live in
// i18n under `aiChat.toolAccess.scopes.<key>.{name,description}` so non-English
// locales can translate them. Unified scopes (calendar_*, mail_*, files_*) are
// provider-agnostic — the platform picks Google or Microsoft at call time based
// on the agent's active grants and the acting user's OAuth connections.
// Docs/Sheets/Excel stay provider-specific (their APIs have no cross-provider
// equivalent).
const KNOWN_TOOL_SCOPES = [
  'calendar_read',
  'calendar_write',
  'mail_read',
  'mail_write',
  'files_read',
  'files_write',
  'google_sheets_read',
  'google_sheets_write',
  'google_docs_read',
  'google_docs_write',
  'microsoft_excel_read',
  'microsoft_excel_write',
  'local_excel_read',
  'local_excel_write',
] as const;

/** Individual row in the tool access table */
const ToolAccessRowComponent: React.FC<{
  row: ToolAccessRow;
  onUpdate: (updates: Partial<ToolAccessRow>) => void;
  onRemove: () => void;
  getModuleConfig: (name: string) => ModuleConfig | undefined;
  getModuleDisplayName: (name: string) => string;
  isModuleReadOnly: boolean;
  moduleToolScopes?: string[];
  showWhoseData: boolean;
  showAgentScope: boolean;
  currentUser?: { id: string; name: string };
  isAdmin: boolean;
  readOnly: boolean;
  compact: boolean;
  showAutoShare: boolean;
}> = ({
  row,
  onUpdate,
  onRemove,
  getModuleConfig,
  getModuleDisplayName,
  isModuleReadOnly,
  moduleToolScopes,
  showWhoseData,
  showAgentScope,
  currentUser,
  isAdmin,
  readOnly,
  compact,
  showAutoShare,
}) => {
  const { t } = useTranslation('aiChat');
  const [scopesExpanded, setScopesExpanded] = useState(false);
  const rowModuleConfig = getModuleConfig(row.module_name);
  const icon = rowModuleConfig?.icon || null;
  const rowColor = rowModuleConfig?.color ?? '#616161';
  const displayName = getModuleDisplayName(row.module_name);
  const hasToolScopes = moduleToolScopes && moduleToolScopes.length > 0;
  const selectedScopes = row.tool_scopes || [];
  const scopesSummary = hasToolScopes
    ? selectedScopes.length === 0
      ? t('toolAccess.allServices', 'All services')
      : `${selectedScopes.length} of ${moduleToolScopes.length}`
    : null;

  // Build scope options based on context
  const scopeOptions = useMemo(() => {
    const options: Array<{ value: ToolDataScope; label: string }> = [];
    if (showAgentScope) {
      options.push({
        value: 'self',
        label: t('toolAccess.scopeAgent', "Agent's own"),
      });
    }
    if (currentUser) {
      options.push({
        value: 'user',
        label: t('toolAccess.scopeUser', "{{name}}'s data", { name: currentUser.name }),
      });
      options.push({
        value: 'user_accessible',
        label: t('toolAccess.scopeUserAccessible', "All {{name}} can access", {
          name: currentUser.name,
        }),
      });
    }
    if (isAdmin) {
      options.push({
        value: 'all',
        label: t('toolAccess.scopeAll', 'All tenant data'),
      });
    }
    return options;
  }, [showAgentScope, currentUser, isAdmin, t]);

  const toggleScope = (scope: string) => {
    if (readOnly) return;
    const current = row.tool_scopes || [];
    const updated = current.includes(scope)
      ? current.filter((s) => s !== scope)
      : [...current, scope];
    onUpdate({ tool_scopes: updated.length > 0 ? updated : undefined });
  };

  return (
    <div>
    <div
      className={cn(
        'grid items-center gap-1.5 px-2 py-1 rounded-md transition-colors group',
        'hover:bg-muted/50',
        row.inherited && 'bg-muted/20',
        showWhoseData
          ? showAutoShare
            ? 'grid-cols-[2fr_auto_1fr_auto_24px]'
            : 'grid-cols-[2fr_auto_1fr_24px]'
          : 'grid-cols-[1fr_auto_24px]',
      )}
    >
      {/* Module name + icon */}
      <div className="flex items-center gap-1.5 min-w-0">
        {hasToolScopes && (
          <button
            type="button"
            className="h-4 w-4 shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground"
            onClick={() => setScopesExpanded(!scopesExpanded)}
          >
            {scopesExpanded
              ? <ChevronDown className="h-3.5 w-3.5" />
              : <ChevronRight className="h-3.5 w-3.5" />
            }
          </button>
        )}
        {icon && (
          <span
            className="w-5 h-5 rounded flex items-center justify-center shrink-0 [&>svg]:h-3 [&>svg]:w-3"
            style={{ backgroundColor: `${rowColor}20`, color: rowColor }}
          >
            {icon}
          </span>
        )}
        <span className={cn('truncate', compact ? 'text-xs' : 'text-sm')}>
          {displayName}
        </span>
        {scopesSummary && (
          <Badge
            variant="outline"
            className="text-[9px] h-4 px-1 shrink-0 text-muted-foreground border-muted-foreground/30 cursor-pointer"
            onClick={() => setScopesExpanded(!scopesExpanded)}
          >
            {scopesSummary}
          </Badge>
        )}
        {row.inherited && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className="text-[9px] h-4 px-1 shrink-0 text-muted-foreground border-muted-foreground/30"
              >
                {t('toolAccess.inherited', 'inherited')}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">
                {t(
                  'toolAccess.inheritedTooltip',
                  'From agent settings. Changes here apply to this conversation only.',
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Access level toggle */}
      <ToggleGroup
        type="single"
        value={row.access_level}
        onValueChange={(value) => {
          if (readOnly || !value) return;
          onUpdate({ access_level: value as McpAccessLevel });
        }}
        className="gap-0 border rounded-md w-[130px]"
      >
        <ToggleGroupItem
          value="read"
          className="text-[10px] h-6 px-1.5 flex-1 rounded-none rounded-l-md data-[state=on]:bg-info/10 data-[state=on]:text-info"
          disabled={readOnly}
          title={t('toolAccess.readTooltip', 'Read-only access')}
        >
          {t('toolAccess.read', 'Read')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="approve"
          className="text-[10px] h-6 px-1.5 flex-1 rounded-none border-l data-[state=on]:bg-warning/10 data-[state=on]:text-warning"
          disabled={readOnly || isModuleReadOnly}
          title={isModuleReadOnly
            ? t('toolAccess.readOnlyModule', 'This module only provides read-only tools')
            : t('toolAccess.approveTooltip', 'Read + Write with user approval required for every write action')}
        >
          {t('toolAccess.approve', 'Approve')}
        </ToggleGroupItem>
        <ToggleGroupItem
          value="readwrite"
          className="text-[10px] h-6 px-1.5 flex-1 rounded-none rounded-r-md border-l data-[state=on]:bg-success/10 data-[state=on]:text-success"
          disabled={readOnly || isModuleReadOnly}
          title={isModuleReadOnly
            ? t('toolAccess.readOnlyModule', 'This module only provides read-only tools')
            : t('toolAccess.readWriteTooltip', 'Read + Write — create, edit, and delete without approval')}
        >
          {t('toolAccess.write', 'Write')}
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Whose data scope */}
      {showWhoseData && (
        <Select
          value={row.data_scope}
          onValueChange={(value) => {
            if (readOnly) return;
            const scope = value as ToolDataScope;
            const updates: Partial<ToolAccessRow> = { data_scope: scope };
            // Auto-set user target for user scopes
            if ((scope === 'user' || scope === 'user_accessible') && currentUser) {
              updates.scope_user_id = currentUser.id;
              updates.scope_user_name = currentUser.name;
            } else {
              updates.scope_user_id = undefined;
              updates.scope_user_name = undefined;
            }
            onUpdate(updates);
          }}
          disabled={readOnly}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scopeOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Auto-share checkbox */}
      {showAutoShare && (
        <div className="flex items-center justify-center w-[52px]">
          <Checkbox
            checked={row.share_created_entities ?? true}
            onCheckedChange={(checked) => {
              if (readOnly) return;
              onUpdate({ share_created_entities: !!checked });
            }}
            disabled={readOnly || row.access_level === 'read'}
            className="h-4 w-4"
          />
        </div>
      )}

      {/* Remove button */}
      {readOnly ? (
        <span className="w-6" />
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={onRemove}
        >
          <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
        </Button>
      )}
    </div>

    {/* Service selection sub-row (for modules with tool_scopes) */}
    {hasToolScopes && scopesExpanded && (
      <div className="ml-6 mb-1 px-2 py-2 rounded-md bg-muted/30 border border-dashed border-border">
        <p className="text-[10px] text-muted-foreground mb-1.5">
          {t('toolAccess.selectServices', 'Select services (none selected = all services):')}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {moduleToolScopes!.map((scope) => {
            const isKnown = (KNOWN_TOOL_SCOPES as readonly string[]).includes(scope);
            const labelName = isKnown
              ? t(`toolAccess.scopes.${scope}.name`)
              : scope.replace(/_/g, ' ');
            const labelDescription = isKnown
              ? t(`toolAccess.scopes.${scope}.description`)
              : '';
            const isSelected = selectedScopes.includes(scope);
            return (
              <Tooltip key={scope}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    disabled={readOnly}
                    onClick={() => toggleScope(scope)}
                    className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] border transition-colors',
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                      readOnly && 'cursor-not-allowed opacity-50',
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                    {labelName}
                  </button>
                </TooltipTrigger>
                {labelDescription && (
                  <TooltipContent side="top">
                    <p className="text-xs">{labelDescription}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </div>
      </div>
    )}
    </div>
  );
};

export default ToolAccessTable;
